import axios from 'axios';
import type { ApiError } from './types';

/** A normalised API failure the UI can branch on (code) and show (message). */
export interface NormalizedApiError extends ApiError {
  /** HTTP status when known (0 for network/unknown). */
  httpStatus: number;
}

/**
 * Turn any thrown value from an axios call into a `{code, message, httpStatus, missing?}` shape.
 * Recognises the platform ApiError body; degrades to a generic code for network/unknown failures so
 * the SPA never renders a raw stack (the "actionable checklist, not raw error" rule).
 */
export function normalizeApiError(err: unknown): NormalizedApiError {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status ?? 0;
    const body = err.response?.data as Partial<ApiError> | undefined;
    if (body && typeof body === 'object' && typeof body.code === 'string') {
      return {
        code: body.code,
        message: body.message ?? err.message,
        traceId: body.traceId,
        missing: Array.isArray(body.missing) ? body.missing : undefined,
        httpStatus: status,
      };
    }
    if (status === 0) {
      return { code: 'NETWORK_ERROR', message: 'The product API is unreachable.', httpStatus: 0 };
    }
    return { code: `HTTP_${status}`, message: err.message, httpStatus: status };
  }
  return {
    code: 'UNKNOWN_ERROR',
    message: err instanceof Error ? err.message : String(err),
    httpStatus: 0,
  };
}

/**
 * True when the failure is a "not wired yet" signal (endpoint absent / mode unavailable).
 *
 * A 404 only counts as "unavailable" when it is a bare/absent-endpoint 404 — i.e. it carries a
 * SYNTHETIC code (`HTTP_404` when the body had no structured code, or a transport `NETWORK_ERROR`).
 * A 404 that carries a real backend error code (e.g. `UAM_USER_NOT_FOUND`) is a DOMAIN error and
 * must surface as itself, not be masked as "the API is not live yet".
 */
export function isNotAvailable(e: NormalizedApiError): boolean {
  const bareNotFound =
    e.httpStatus === 404 && (e.code.startsWith('HTTP_') || e.code === 'NETWORK_ERROR');
  return (
    bareNotFound ||
    e.httpStatus === 501 ||
    e.code === 'MODE_NOT_IMPLEMENTED' ||
    e.code === 'MODE_SUBSTRATE_UNAVAILABLE' ||
    e.code === 'NETWORK_ERROR'
  );
}
